/**
 * What each clinic earned, what it cost, and whether they add up.
 *
 * The screen the whole system exists for. It is not a separate set of books
 * that somebody reconciles at month end — it is one grouping over the same
 * journal lines the company accounts are made of, which is why the clinic rows
 * add up to the company total by construction.
 *
 * Two things are shown rather than hidden.
 *
 * **Unassigned is a row.** Spend posted with no clinic on it is real spend that
 * happened somewhere, and dropping it would make the rows sum to less than the
 * company with no visible reason. The useful response to a large Unassigned
 * figure is to go and fix the postings, and that only happens if somebody can
 * see it — so it opens, and what opens is the list of accounts to fix.
 *
 * **A clinic that earned nothing has no margin.** Not zero: zero would say it
 * broke even.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { Card, ErrorNote, Loading, Note } from '../components/ui'

type Axis = 'location' | 'department' | 'cost_center'

interface Segment {
  segment_id: string | null
  code: string | null
  name: string
  income_paise: number
  expense_paise: number
  profit_paise: number
  margin_bp: number | null
}

interface SegmentStatement {
  axis: string
  fy_code: string
  since: string
  upto: string
  segments: Segment[]
  company_income_paise: number
  company_expense_paise: number
  company_profit_paise: number
  difference_paise: number
  ties_out: boolean
  unassigned_expense_paise: number
}

interface SegmentAccount {
  account_id: string
  code: string
  name: string
  account_type: string
  amount_paise: number
}

const AXES: { key: Axis; label: string; blurb: string }[] = [
  { key: 'location', label: 'By clinic', blurb: 'Where the work was done' },
  { key: 'department', label: 'By department', blurb: 'What kind of work it was' },
  { key: 'cost_center', label: 'By cost centre', blurb: 'How the group chooses to group cost' },
]

function currentFy(): string {
  const now = new Date()
  const start = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`
}

function margin(bp: number | null): string {
  if (bp === null) return '—'
  return `${(bp / 100).toFixed(1)}%`
}

export default function Performance() {
  const [axis, setAxis] = useState<Axis>('location')
  const [fyCode, setFyCode] = useState(currentFy)
  const [open, setOpen] = useState<{ id: string | null; name: string } | null>(null)

  const statement = useQuery({
    queryKey: ['segments', axis, fyCode],
    queryFn: () =>
      api.get<SegmentStatement>(`/reports/segments?fy_code=${fyCode}&axis=${axis}`),
  })
  const detail = useQuery({
    queryKey: ['segment-accounts', axis, fyCode, open?.id ?? 'unassigned'],
    queryFn: () =>
      api.get<SegmentAccount[]>(
        `/reports/segments/accounts?fy_code=${fyCode}&axis=${axis}` +
          (open?.id ? `&segment_id=${open.id}` : ''),
      ),
    enabled: open !== null,
  })

  const data = statement.data
  const chosen = AXES.find((a) => a.key === axis)

  return (
    <>
      <ErrorNote error={statement.error} />

      <Card title="Period">
        <div className="row">
          <div>
            <span className="caption">Cut by</span>
            <select
              value={axis}
              onChange={(e) => {
                setAxis(e.target.value as Axis)
                setOpen(null)
              }}
            >
              {AXES.map((a) => (
                <option key={a.key} value={a.key}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <span className="caption">Financial year</span>
            <input value={fyCode} onChange={(e) => setFyCode(e.target.value)} />
          </div>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          {chosen?.blurb}. The same journal lines as the company accounts, grouped a
          different way — so these figures cannot disagree with the company's.
        </p>
      </Card>

      {statement.isLoading ? (
        <Loading what="Working out what each one earned" />
      ) : (
        data && (
          <>
            {!data.ties_out && (
              <Note kind="error">
                The rows below come to {formatInr(data.difference_paise)} more than the
                company total. They are built from the same lines, so a difference means a
                line is being counted twice. This is a defect, not a reporting choice.
              </Note>
            )}

            <Card
              title={`Profit by ${axis.replace('_', ' ')}`}
              hint={`${data.since} to ${data.upto}`}
              tight
              actions={
                data.ties_out ? (
                  <span className="faint">adds up to the company accounts</span>
                ) : undefined
              }
            >
              {data.segments.length === 0 ? (
                <div className="empty">Nothing posted in this year.</div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th />
                      <th>Name</th>
                      <th className="num">Earned</th>
                      <th className="num">Cost</th>
                      <th className="num">Profit</th>
                      <th className="num">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.segments.map((s) => (
                      <tr key={s.segment_id ?? 'unassigned'}>
                        <td className="mono muted">{s.code ?? '—'}</td>
                        <td>
                          <button
                            className="small"
                            onClick={() => setOpen({ id: s.segment_id, name: s.name })}
                          >
                            {s.name}
                          </button>
                          {s.segment_id === null && (
                            <div className="faint">
                              posted with no {axis.replace('_', ' ')} on it
                            </div>
                          )}
                        </td>
                        <td className="num muted">{formatInr(s.income_paise)}</td>
                        <td className="num muted">{formatInr(s.expense_paise)}</td>
                        <td className="num">
                          <strong>{formatInr(s.profit_paise)}</strong>
                        </td>
                        <td className="num muted">{margin(s.margin_bp)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td />
                      <td>
                        <strong>The company</strong>
                      </td>
                      <td className="num">
                        <strong>{formatInr(data.company_income_paise)}</strong>
                      </td>
                      <td className="num">
                        <strong>{formatInr(data.company_expense_paise)}</strong>
                      </td>
                      <td className="num">
                        <strong>{formatInr(data.company_profit_paise)}</strong>
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              )}
            </Card>

            {data.unassigned_expense_paise > 0 && (
              <Note kind="warn">
                {formatInr(data.unassigned_expense_paise)} of cost carries no{' '}
                {axis.replace('_', ' ')}. It is in the company total and in the Unassigned
                row, so nothing is lost — but it is cost no clinic is answering for. Open
                the Unassigned row to see which accounts, then fix the postings.
              </Note>
            )}
          </>
        )
      )}

      {open && (
        <Card
          title={`${open.name} — what made it up`}
          hint={fyCode}
          actions={
            <button className="small" onClick={() => setOpen(null)}>
              Close
            </button>
          }
          tight
        >
          {detail.isLoading ? (
            <Loading />
          ) : (detail.data ?? []).length === 0 ? (
            <div className="empty">Nothing posted here.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Account</th>
                  <th>Kind</th>
                  <th className="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(detail.data ?? []).map((row) => (
                  <tr key={row.account_id}>
                    <td className="mono">{row.code}</td>
                    <td>{row.name}</td>
                    <td className="muted">{row.account_type}</td>
                    <td className="num">{formatInr(row.amount_paise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="faint">
            The same query as the figure above, narrowed by one equality — so a total and
            the detail you open to check it cannot disagree.
          </p>
        </Card>
      )}
    </>
  )
}
